import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { Form, FormikErrors, FormikProvider, useFormik } from "formik";
import { Taxonomy } from "@common/categories";
import { toIsoDate } from "@common/helpers/date";
import { fromMonetaryToInt } from "@common/helpers/monetary";
import { Button } from "@design-system/Button/Button";
import { useConstant } from "@hooks/useConstant";
import { HStack } from "@design-system/HStack/HStack";
import { PlainModal, StandardModalHeader } from "@design-system/Modal/Modal";
import { VStack } from "@design-system/VStack/VStack";
import { CategoryStructure, useCategories } from "@hooks/useCategories";
import { useEntitiesById } from "@hooks/useEntities";
import { useIsGlFirst } from "@hooks/useIsGlFirst";
import { useUncategorizedCategory } from "@hooks/useUncategorizedCategory";
import { HOTKEYS_SCOPES } from "@hooks/keyboard/useKeyboardCommand";
import { useScopeSwitch } from "@hooks/keyboard/useScopeSwitch";
import { useScope } from "@state/scopeContext.lib";
import { useLockDateState } from "@api/lock.api";
import {
    clampToMinDateOnly,
    getFirstUnlockedDateOnly,
    isLockedByDateOnly,
} from "@helpers/lock";
import {
    RecordInvoicePaymentForm,
    RecordInvoicePaymentFormFields,
} from "./RecordInvoicePaymentFormFields";
import {
    approveAccrualReviewRequest,
    useApproveWithScheduleResolutions,
} from "@components/accounting/schedules/linkedConflict/useApproveWithScheduleResolutions";
import { hasDocumentsLeftAfterUnmatch } from "@components/accounting/transactionMatch/schedule/matchResolutions";
import {
    buildInvoicePaymentAllocations,
    InvoicePaymentLine,
} from "./buildInvoicePaymentAllocations";
import { useRecordInvoicePayment } from "./useRecordInvoicePayment";

interface RecordInvoicePaymentModalProps {
    show: boolean;
    invoiceId: string;
    invoiceNumber?: string | null;
    invoiceEntityId: number;
    openAmount: number;
    lines?: InvoicePaymentLine[];
    counterpartyId?: string | null;
    onCancel: () => void;
    onRecorded: () => void;
}

// The modal keyboard scope must be active only while the dialog is open.
// PlainModal unmounts its children on close, so hosting the scope switch in a
// child rendered inside the modal releases the scope (and re-enables MAIN) when
// it closes, letting the modal stay mounted for the close animation.
const RecordInvoicePaymentModalScope: React.FC = () => {
    useScopeSwitch(HOTKEYS_SCOPES.GENERAL_MODAL);
    return null;
};

export const RecordInvoicePaymentModal: React.FC<
    RecordInvoicePaymentModalProps
> = ({
    show,
    invoiceId,
    invoiceNumber,
    invoiceEntityId,
    openAmount,
    lines = [],
    counterpartyId,
    onCancel,
    onRecorded,
}) => {
    const { workspaceId } = useScope();
    const { approveWithScheduleResolutions, scheduleResolutionModals } =
        useApproveWithScheduleResolutions();
    const recordPayment = useRecordInvoicePayment({
        approve: ({ workspaceId: approveWorkspaceId, body }) =>
            approveWithScheduleResolutions({
                request: approveAccrualReviewRequest({
                    workspaceId: approveWorkspaceId,
                    body,
                }),
                hasWorkLeft: (resolutions) =>
                    hasDocumentsLeftAfterUnmatch({ body, resolutions }),
            }),
    });
    const entitiesById = useEntitiesById({ excludeMockEntities: true });
    const categories = useCategories({
        structure: CategoryStructure.FLAT,
        onlyStandardCategories: true,
    });
    const incomeCategory = categories.find(
        (category) =>
            category.taxonomy === Taxonomy.income &&
            category.parentCategoryId == null,
    );
    const uncategorizedCategory = useUncategorizedCategory();
    const { enabled: isGlFirst } = useIsGlFirst();
    // GL-first rejects a categorized transaction without a CoA account, so the
    // payment is created uncategorized (defaulting to Uncategorized Income);
    // matching then assigns each invoice line's income account.
    const paymentCategory = isGlFirst ? uncategorizedCategory : incomeCategory;

    const invoiceEntity = entitiesById.get(invoiceEntityId);
    const {
        lockDate,
        isResolved: isLockResolved,
        isError: isLockError,
    } = useLockDateState(invoiceEntityId);
    const firstUnlockedDate = getFirstUnlockedDateOnly(lockDate);
    const today = useConstant(() => toIsoDate(new Date()));
    const defaultPaymentDate = clampToMinDateOnly(today, firstUnlockedDate);

    const initialValues = useMemo<RecordInvoicePaymentForm>(
        () => ({
            entityId: invoiceEntityId,
            amount: openAmount,
            paymentDate: defaultPaymentDate,
            description: invoiceNumber
                ? `Payment for invoice ${invoiceNumber}`
                : "Invoice payment",
        }),
        [invoiceEntityId, openAmount, defaultPaymentDate, invoiceNumber],
    );

    const formik = useFormik<RecordInvoicePaymentForm>({
        initialValues,
        validate: (values) => {
            const errors: FormikErrors<RecordInvoicePaymentForm> = {};

            if (values.amount == null || values.amount <= 0) {
                errors.amount = "Enter an amount";
            } else if (
                fromMonetaryToInt(values.amount) !==
                fromMonetaryToInt(openAmount)
            ) {
                errors.amount = `Amount must match the open balance ${openAmount}`;
            }

            if (!values.paymentDate) {
                errors.paymentDate = "Enter a payment date";
            } else if (isLockedByDateOnly(lockDate, values.paymentDate)) {
                errors.paymentDate =
                    "Date falls within a locked accounting period";
            }

            return errors;
        },
        onSubmit: async (values) => {
            if (
                !workspaceId ||
                values.amount == null ||
                paymentCategory == null
            ) {
                return;
            }

            const outcome = await recordPayment.mutateAsync({
                workspaceId,
                invoiceId,
                entityId: values.entityId,
                amount: values.amount,
                paymentDate: values.paymentDate,
                description: values.description,
                categoryId: paymentCategory.id,
                counterpartyId,
                lineItemAllocations: buildInvoicePaymentAllocations({
                    lines,
                    paymentAmount: values.amount,
                }),
            });
            if (outcome !== "approved") {
                return;
            }
            onRecorded();
            onCancel();
        },
    });

    // Reset on open so edits discarded via Cancel don't reappear: the modal
    // stays mounted for the close animation, so Formik state would otherwise
    // persist across open/close cycles. resetForm is identity-stable.
    const { resetForm, setFieldValue } = formik;
    const wasShownRef = useRef(false);
    useEffect(() => {
        if (show && !wasShownRef.current) {
            resetForm({ values: initialValues });
        }
        wasShownRef.current = show;
    }, [show, initialValues, resetForm]);

    const isPaymentDateUntouched =
        formik.values.paymentDate === formik.initialValues.paymentDate;
    useEffect(() => {
        if (show && isPaymentDateUntouched) {
            setFieldValue("paymentDate", defaultPaymentDate);
        }
    }, [show, isPaymentDateUntouched, defaultPaymentDate, setFieldValue]);

    const handleHide = useCallback(() => {
        if (formik.isSubmitting) {
            return;
        }
        onCancel();
    }, [formik.isSubmitting, onCancel]);

    const title = invoiceNumber
        ? `Mark invoice ${invoiceNumber} paid`
        : "Mark invoice paid";

    return (
        <>
            <PlainModal show={show} onHide={handleHide} size="md">
                <FormikProvider value={formik}>
                    <Form>
                        <RecordInvoicePaymentModalScope />
                        <StandardModalHeader>{title}</StandardModalHeader>
                        <VStack gap={16}>
                            <p className="text-gray-500">
                                A cash transaction will be created and matched
                                to this invoice.
                            </p>
                            <RecordInvoicePaymentFormFields
                                entity={invoiceEntity}
                                minPaymentDate={firstUnlockedDate}
                            />
                        </VStack>
                        <HStack gap={12} justify="end">
                            <Button
                                type="button"
                                variant="secondary"
                                onClick={handleHide}
                                size="xl"
                                disabled={formik.isSubmitting}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="primary"
                                type="submit"
                                size="xl"
                                disabled={
                                    formik.isSubmitting ||
                                    paymentCategory == null ||
                                    (!isLockResolved && !isLockError) ||
                                    !workspaceId
                                }
                            >
                                Mark as paid
                            </Button>
                        </HStack>
                    </Form>
                </FormikProvider>
            </PlainModal>
            {scheduleResolutionModals}
        </>
    );
};
